import { ISSUE_MGMT_WRITE_PERMISSION, type IssueDetail } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { ORPCError } from '@orpc/client';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DecideDialog, type DecisionChoice } from '../components/issue-mgmt/DecideDialog.js';
import { DoneDialog } from '../components/issue-mgmt/DoneDialog.js';
import { IssueCases } from '../components/issue-mgmt/IssueCases.js';
import { IssueContentCards } from '../components/issue-mgmt/IssueContentCards.js';
import { IssueHeader } from '../components/issue-mgmt/IssueHeader.js';
import { IssueOthers } from '../components/issue-mgmt/IssueOthers.js';
import { IssueVoices } from '../components/issue-mgmt/IssueVoices.js';
import { LinkCaseDialog } from '../components/issue-mgmt/LinkCaseDialog.js';
import { MergeDialog } from '../components/issue-mgmt/MergeDialog.js';
import { useIssueFieldSave } from '../components/issue-mgmt/useIssueFieldSave.js';
import { useIssueToast } from '../components/issue-mgmt/useIssueToast.js';
import { useIssueDetail, useIssueDomains, useIssueMgmtWrite } from '../hooks/useIssueMgmt.js';
import { useIssueCases } from '../hooks/useIssueTodo.js';
import { usePermissions } from '../hooks/usePermissions.js';

/**
 * 開いている小窓。
 * @graph-connects none
 */
type OpenDialog =
  | { kind: 'decide'; decision: DecisionChoice }
  | { kind: 'done' }
  | { kind: 'merge' }
  | { kind: 'link_case' }
  | null;

/**
 * 「判定を変える」で開く判定（今の判定。未判定なら対応する）。
 * @graph-connects none
 */
function currentDecision(issue: IssueDetail): DecisionChoice {
  return issue.verdict === 'on_hold' || issue.verdict === 'rejected' ? issue.verdict : 'accepted';
}

/**
 * 課題管理の「課題」タブの詳細（SC-03）。見出しと次にやること → 中身3項目 → 声 → 案件 → その他（畳む）の順に出す。
 *
 * 編集キーの人は、次にやることの大ボタン（判定・直ったかはやることのカードへ移る、必須項目・案件の紐づけ・対応したは小窓、
 * 返信済み・Backlogで確かめたはその場で記録）・判定を変える・タイトルと中身のその場の編集・テーマと領域などの付与・
 * 案件を紐づける／外す・別の課題にまとめる・統合の取り消し・再発した を操作できる。保存はトーストで知らせ、
 * 取り消せる操作はトーストから元に戻せる。
 * 統合済みの課題を開くと、統合先へのリンクと「取り消す」を最上段に出す。
 *
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題1件を理解して次の操作（判定・中身の確かめ・まとめる・対応した・再発した）を進める詳細画面
 * @graph-connects useIssueDetail [calls] 課題の詳細
 * @graph-connects useIssueDomains [calls] 領域の選択肢
 * @graph-connects useIssueMgmtWrite [calls] reopen・unmerge・markReplied・checkCase
 * @graph-connects useIssueCases [calls] 案件の数と完了の数
 * @graph-connects usePermissions [calls] 編集キーの有無
 * @graph-connects IssueHeader [calls] 見出しと次にやること
 * @graph-connects IssueContentCards [calls] 中身3項目
 * @graph-connects IssueVoices [calls] 声
 * @graph-connects IssueCases [calls] 案件
 * @graph-connects IssueOthers [calls] その他
 * @graph-connects LinkCaseDialog [calls] 案件を紐づける小窓
 */
export function IssueMgmtDetailPage({ now = new Date() }: { now?: Date }) {
  const { t } = useTranslation();
  const params: { issueId?: string } = useParams({ strict: false });
  const issueId = params.issueId ?? '';
  const detail = useIssueDetail(issueId);
  const domains = useIssueDomains();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canEdit = !permissionsLoading && hasPermission(ISSUE_MGMT_WRITE_PERMISSION);
  const save = useIssueFieldSave(issueId);
  const toast = useIssueToast();
  const reopen = useIssueMgmtWrite('reopen');
  const unmerge = useIssueMgmtWrite('unmerge');
  const markReplied = useIssueMgmtWrite('markReplied');
  const checkCase = useIssueMgmtWrite('checkCase');
  const cases = useIssueCases(issueId, issueId !== '');
  // 開いている小窓は、開いた課題のときだけ有効にする（別の課題へ移ったら持ち越さない）
  const [opened, setOpened] = useState<{ issueId: string; dialog: OpenDialog }>({
    issueId,
    dialog: null,
  });
  const dialog = opened.issueId === issueId ? opened.dialog : null;
  const setDialog = (next: OpenDialog) => {
    setOpened({ issueId, dialog: next });
  };

  const back = (
    <Link
      to="/issue-mgmt/issues"
      className="inline-flex items-center gap-1 text-sm text-[var(--color-accent-primary)] hover:underline"
    >
      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      {t('issueMgmt.detail.back')}
    </Link>
  );

  if (detail.isError) {
    const notFound = detail.error instanceof ORPCError && detail.error.code === 'NOT_FOUND';
    return (
      <div className="space-y-4">
        {back}
        <div className="space-y-2 rounded-xl border border-[var(--color-border)] p-6 text-center">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {notFound ? t('issueMgmt.detail.notFound') : t('issueMgmt.common.loadError')}
          </p>
          {!notFound && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void detail.refetch();
              }}
            >
              {t('issueMgmt.common.retry')}
            </Button>
          )}
        </div>
      </div>
    );
  }
  if (detail.data === undefined) {
    return (
      <div className="space-y-4">
        {back}
        <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
          {t('issueMgmt.common.loading')}
        </p>
      </div>
    );
  }

  const issue = detail.data;
  const domainOptions = domains.data?.domains ?? [];
  const close = () => {
    setDialog(null);
  };

  return (
    <div className="space-y-4">
      {back}
      {issue.mergedInto !== null && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--color-warning)] bg-[color-mix(in_srgb,var(--color-warning)_10%,transparent)] px-4 py-3 text-sm">
          <span className="font-medium text-[var(--color-text-primary)]">
            {t('issueMgmt.detail.mergedInto', { title: issue.mergedInto.title })}
          </span>
          <Link
            to="/issue-mgmt/issues/$issueId"
            params={{ issueId: issue.mergedInto.id }}
            className="text-[var(--color-accent-primary)] hover:underline"
          >
            {t('issueMgmt.detail.openTarget')}
          </Link>
          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              loading={unmerge.isPending}
              onClick={() => {
                unmerge.mutate(
                  { issueId: issue.id, revision: issue.revision },
                  {
                    onSuccess: (result) => {
                      toast.notifySaved(t('issueMgmt.detail.unmerged', { title: issue.title }), {
                        operationId: result.operationId,
                      });
                    },
                    onError: toast.notifyError,
                  },
                );
              }}
            >
              {t('issueMgmt.detail.unmerge')}
            </Button>
          )}
        </div>
      )}
      <IssueHeader
        key={`header-${issue.id}`}
        issue={issue}
        canEdit={canEdit}
        save={save}
        now={now}
        caseProgress={
          cases.data === undefined
            ? null
            : { total: cases.data.total, completed: cases.data.completed }
        }
        actions={{
          onDecide: (mode) => {
            setDialog({
              kind: 'decide',
              decision: mode === 'change' ? currentDecision(issue) : 'accepted',
            });
          },
          onDone: () => {
            setDialog({ kind: 'done' });
          },
          onLinkCase: () => {
            setDialog({ kind: 'link_case' });
          },
          onReply: () => {
            const pointIds = issue.voices
              .filter((voice) => voice.postedAt !== null && voice.repliedAt === null)
              .map((voice) => voice.pointId);
            if (pointIds.length === 0) return;
            markReplied.mutate(
              { issueId: issue.id, pointIds },
              {
                onSuccess: (result) => {
                  toast.notifySaved(t('issueMgmt.card.repliedSaved', { title: issue.title }), {
                    operationId: result.operationId,
                  });
                },
                onError: toast.notifyError,
              },
            );
          },
          onCheckCase: (caseKey) => {
            checkCase.mutate(
              { issueId: issue.id, caseKey },
              {
                onSuccess: (result) => {
                  toast.notifySaved(t('issueMgmt.card.checkedSaved', { title: issue.title }), {
                    operationId: result.operationId,
                    detail: result.recorded
                      ? t('issueMgmt.card.checkedDetail')
                      : t('issueMgmt.card.checkedRetry'),
                  });
                },
                onError: toast.notifyError,
              },
            );
          },
          onReopen: () => {
            reopen.mutate(
              { issueId: issue.id },
              {
                onSuccess: (result) => {
                  toast.notifySaved(t('issueMgmt.next.reopened', { title: issue.title }), {
                    operationId: result.operationId,
                  });
                },
                onError: toast.notifyError,
              },
            );
          },
        }}
      />
      {/* 部品の手元の状態（その場の編集の下書き・声や履歴の表示件数など）を、別の課題へ持ち越さない */}
      <IssueContentCards
        key={`content-${issue.id}`}
        issue={issue}
        canEdit={canEdit && issue.mergedInto === null}
        save={save}
      />
      <IssueVoices key={`voices-${issue.id}`} issue={issue} now={now} />
      <IssueCases
        key={`cases-${issue.id}`}
        issue={issue}
        canEdit={canEdit && issue.mergedInto === null}
        onLinkCase={() => {
          setDialog({ kind: 'link_case' });
        }}
        now={now}
      />
      <IssueOthers
        key={`others-${issue.id}`}
        issue={issue}
        canEdit={canEdit && issue.mergedInto === null}
        save={save}
        domainOptions={domainOptions}
        onMerge={() => {
          setDialog({ kind: 'merge' });
        }}
        now={now}
      />
      {dialog?.kind === 'decide' && (
        <DecideDialog issue={issue} initialDecision={dialog.decision} onClose={close} now={now} />
      )}
      {dialog?.kind === 'done' && <DoneDialog issue={issue} onClose={close} />}
      {dialog?.kind === 'merge' && <MergeDialog issue={issue} onClose={close} />}
      {dialog?.kind === 'link_case' && <LinkCaseDialog issue={issue} onClose={close} now={now} />}
    </div>
  );
}
